import type { UnitOfMeasure } from '../types/unit-of-measure'
import type { ParsedValue } from '../types/parsed-value'
import {
  MAX_DECIMAL_PLACES,
  PT_PER_INCH,
  PT_PER_PICA,
  ROUND_PASTED_TO,
} from '../constants/unit-conversion-limits'
import { VALIDATION_ERRORS } from '../constants/validation-errors'
import { isSameUnitAsTarget } from './format-points-for-display'

/** Conversion constants (points as base unit; 1 px = 1 pt in canvas) */

/** Regex for unit suffixes - case-insensitive, supports optional space between number and unit */
export const UNIT_PATTERNS = {
  /** Inch symbol " (e.g. 8.5") - check before word-based patterns */
  inchSymbol: /^([\d.]+)\s*"$/,
  /** Inches: longest first (inches|inch|in) to avoid partial match */
  inch: /\s*(inches|inch|in)\s*$/i,
  /** Points: longest first (points|pts|pt) */
  point: /\s*(points|pts|pt)\s*$/i,
  /** Picas: longest first (picas|pica|p) */
  pica: /\s*(picas|pica|p)\s*$/i,
  /** Pica+point composite: XpY - both parts can be decimal. E.g. 6p0, 0.1p0, 0p0.1, 0p.125 */
  picaPoint: /^(-?)([\d.]+)\s*p\s*([\d.]+)\s*$/i,
  /** Invalid pica notation with multiple p separators (e.g. 6pp6) */
  invalidPicaPoint: /p.*p/i,
}

/** Returns decimal places in a numeric string (e.g. "1.2500" -> 4, "1" -> 0) */
export function getDecimalPlaces(str: string): number {
  const parts = str.split('.')

  return parts.length === 2 ? parts[1].length : 0
}

/** Round num to 2 decimals when it has more than 4 decimal places */
export function roundIfExcessDecimals(num: number, decimalPlaces: number): number {
  if (decimalPlaces > MAX_DECIMAL_PLACES) {
    const factor = 10 ** ROUND_PASTED_TO

    return Math.round(num * factor) / factor
  }

  return num
}

/** Carry whole picas when the points portion is 12 or more (e.g. 6p12 → 7p0). */
export function normalizePicaPointParts(
  picas: number,
  points: number
): { picas: number; points: number } {
  if (points >= PT_PER_PICA) {
    const carry = Math.floor(points / PT_PER_PICA)

    return { picas: picas + carry, points: points - carry * PT_PER_PICA }
  }

  return { picas, points }
}

/**
 * Parse user input that may include a unit suffix.
 * Accepted notations (case-insensitive):
 * - Inches: in, In, Inch, Inches, INCHES, or " (e.g. 8.5")
 * - Picas: p, pica, picas, PICAS, or XpY (e.g. 5p6 for 5 picas 6 points)
 * - Points: pt, Pt, pts, Pts, points, Points
 *
 * When no unit suffix is present, the value is interpreted as being in targetUnit.
 */
export function parseValueWithUnit(input: string, targetUnit?: UnitOfMeasure): ParsedValue {
  const trimmed = input.trim()

  if (trimmed === '') {
    return { points: 0, hadUnit: false }
  }

  if (UNIT_PATTERNS.invalidPicaPoint.test(trimmed)) {
    return { points: 0, hadUnit: false, error: VALIDATION_ERRORS.INVALID_NUMBER }
  }

  // Pica+point composite (XpY): accepts decimal picas, 0, 0p0, 0.1-0.9, 0p0.1-0p0.9
  // 6p12 → 7p0: points >= 12 auto-convert.
  const picaPointMatch = trimmed.match(UNIT_PATTERNS.picaPoint)

  if (picaPointMatch) {
    const isNegative = picaPointMatch[1] === '-'
    const picasStr = picaPointMatch[2]
    const pointsStr = picaPointMatch[3]
    const picas = parseFloat(picasStr)
    const points = parseFloat(pointsStr)

    if (!Number.isFinite(picas) || !Number.isFinite(points) || points < 0) {
      return {
        points: 0,
        hadUnit: true,
        inputUnit: 'picaPoint',
        error: VALIDATION_ERRORS.INVALID_NUMBER,
      }
    }

    const normalized = normalizePicaPointParts(Math.abs(picas), points)
    let totalPoints = normalized.picas * PT_PER_PICA + normalized.points

    if (isNegative) {
      totalPoints = -totalPoints
    }

    const dp = Math.max(getDecimalPlaces(picasStr), getDecimalPlaces(pointsStr))

    totalPoints = roundIfExcessDecimals(totalPoints, dp)

    return {
      points: totalPoints,
      hadUnit: true,
      inputUnit: 'picaPoint',
      decimalPlaces: Math.min(dp, MAX_DECIMAL_PLACES),
    }
  }

  // Strip known unit suffix and parse number
  let numStr = trimmed
  let inputUnit: ParsedValue['inputUnit'] | undefined

  const inchSymbolMatch = trimmed.match(UNIT_PATTERNS.inchSymbol)

  if (inchSymbolMatch) {
    numStr = inchSymbolMatch[1].trim()
    inputUnit = 'inch'
  } else if (UNIT_PATTERNS.inch.test(trimmed)) {
    numStr = trimmed.replace(UNIT_PATTERNS.inch, '').trim()
    inputUnit = 'inch'
  } else if (UNIT_PATTERNS.point.test(trimmed)) {
    numStr = trimmed.replace(UNIT_PATTERNS.point, '').trim()
    inputUnit = 'point'
  } else if (UNIT_PATTERNS.pica.test(trimmed)) {
    numStr = trimmed.replace(UNIT_PATTERNS.pica, '').trim()
    inputUnit = 'pica'
  }

  const dp = getDecimalPlaces(numStr)
  let num = parseFloat(numStr)

  if (!Number.isFinite(num)) {
    return { points: 0, hadUnit: !!inputUnit, inputUnit, error: VALIDATION_ERRORS.INVALID_NUMBER }
  }

  // Unrecognized unit strings trigger no conversion and may show a validation error
  if (inputUnit === undefined && /[a-zA-Z]/.test(trimmed)) {
    return { points: 0, hadUnit: false, error: VALIDATION_ERRORS.UNRECOGNIZED_UNIT }
  }

  num = roundIfExcessDecimals(num, dp)

  let points: number

  switch (inputUnit) {
    case 'inch':
      points = num * PT_PER_INCH
      break
    case 'point':
      points = num
      break
    case 'pica':
      points = num * PT_PER_PICA
      break
    default:
      // No unit - assume target unit (default to Points)
      if (targetUnit === 'Inches') {
        points = num * PT_PER_INCH
      } else if (targetUnit === 'Picas') {
        points = num * PT_PER_PICA
      } else {
        points = num // Points or undefined
      }

      break
  }

  return {
    points,
    hadUnit: !!inputUnit,
    inputUnit,
    decimalPlaces: Math.min(dp, MAX_DECIMAL_PLACES),
  }
}

/** Decimal places to preserve on blur when the user entered an explicit unit/precision. */
export function parsedDisplayDecimalPlaces(
  parsed: ParsedValue,
  targetUnit: UnitOfMeasure
): number | undefined {
  if (parsed.decimalPlaces === undefined) {
    return undefined
  }

  if (!parsed.hadUnit || isSameUnitAsTarget(parsed, targetUnit)) {
    return parsed.decimalPlaces
  }

  return undefined
}
